import { useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { formatK } from "@/components/tools/chem-physical/gibbs-panel";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import {
  cellState,
  electroMessages,
  type NernstStrings,
} from "@/components/tools/chem-physical/nernst-panel";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { FARADAY, GAS_CONSTANT } from "@/lib/tool-kit/chem-physical/constants";
import { solveNernst } from "@/lib/tool-kit/chem-physical/electro";
import {
  combineHalfCells,
  findHalfCell,
  HALF_CELLS,
  speciesText,
} from "@/lib/tool-kit/chem-physical/half-cells";
import {
  TEMPERATURE_UNITS,
  toKelvin,
} from "@/lib/tool-kit/chem-physical/units";
import { formatPlain, formatSig } from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

const DEFAULT_SIG = 4;
const TEMPERATURE_OPTIONS = TEMPERATURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));

function temperatureProblem(text: string, kelvin: number | null): string {
  if (text.trim() === "") {
    return "missing";
  }
  return kelvin === null ? "unreadable" : "belowZero";
}

function CellAnswer({
  cathodeId,
  anodeId,
  tText,
  tUnit,
  qText,
  sig,
  strings,
}: {
  cathodeId: string;
  anodeId: string;
  tText: string;
  tUnit: string;
  qText: string;
  sig: number;
  strings: NernstStrings;
}) {
  const fmt = useToolNumber();
  const cathode = findHalfCell(cathodeId);
  const anode = findHalfCell(anodeId);
  const cell = combineHalfCells(cathodeId, anodeId);
  const typedT = parseMeasuredNumber(tText);
  const kelvin = typedT === null ? null : toKelvin(typedT, tUnit);
  if (!(cathode && anode && cell)) {
    return <Notice>{strings.sameHalfCell}</Notice>;
  }
  const p = (value: number) => formatPlain(fmt, value);
  const volts = (value: number) => `${formatSig(fmt, value, sig)} V`;
  const state = {
    dead: strings.cellDead,
    galvanic: strings.galvanic,
    electrolytic: strings.nonSpontaneousCell,
  }[cellState(cell.e0)];
  const dg0 = -cell.n * FARADAY * cell.e0;
  const validT = kelvin !== null && kelvin > 0;
  const log10K = validT
    ? (cell.n * FARADAY * cell.e0) / (GAS_CONSTANT * kelvin * Math.LN10)
    : null;
  const nernst =
    qText.trim() === ""
      ? null
      : solveNernst({
          unknown: "e",
          n: String(cell.n),
          text: { e: "", e0: String(cell.e0), q: qText, t: tText },
          unit: { e: "V", e0: "V", t: tUnit },
        });
  const electrons = (n: number) => (n === 1 ? "e⁻" : `${n}e⁻`);
  const cathodeLine = `${speciesText(cathode.left)} + ${electrons(cathode.n)} → ${speciesText(cathode.right)}`;
  const anodeLine = `${speciesText(anode.right)} → ${speciesText(anode.left)} + ${electrons(anode.n)}`;
  const multiplied = cell.cathodeMultiplier !== 1 || cell.anodeMultiplier !== 1;
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.cellE0} value={volts(cell.e0)} />
        <StatCard label={strings.termN} value={p(cell.n)} />
        <StatCard label={strings.cellState} value={state} />
        <StatCard
          label={strings.standardDeltaG}
          value={`${formatSig(fmt, dg0 / 1000, sig)} kJ/mol`}
        />
        {log10K === null ? null : (
          <StatCard
            label={strings.equilibriumK}
            value={formatK(fmt, log10K, sig)}
          />
        )}
        {nernst?.ok ? (
          <StatCard label={strings.cellENow} value={volts(nernst.e)} />
        ) : null}
      </StatGrid>
      <div className="space-y-1 rounded-lg border border-border bg-card p-4 text-foreground text-sm">
        <p className="font-medium">{strings.overallReaction}</p>
        <p className="font-mono">
          {speciesText(cell.reactants)} → {speciesText(cell.products)}
        </p>
      </div>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {t(strings.cathodeLine, {
            reaction: cathodeLine,
            e0: `${p(cathode.e0)} V`,
          })}
        </p>
        <p>
          {t(strings.anodeLine, {
            reaction: anodeLine,
            e0: `${p(anode.e0)} V`,
          })}
        </p>
        {multiplied ? (
          <p>
            {t(strings.multiplierLine, {
              a: p(cell.cathodeMultiplier),
              b: p(cell.anodeMultiplier),
              n: p(cell.n),
            })}
          </p>
        ) : null}
        <p>
          {t(strings.cellEquation, {
            c: p(cathode.e0),
            a: p(anode.e0),
            result: `${formatSig(fmt, cell.e0, sig + 1)} V`,
          })}
        </p>
        <p>
          {t(strings.deltaGLine, { n: p(cell.n), e: p(cell.e0), dg: p(dg0) })}
        </p>
      </div>
      {nernst && !nernst.ok ? (
        <FailureNotice
          failure={nernst}
          labels={{ q: strings.termQ, t: strings.termT }}
          messages={electroMessages(strings)}
        />
      ) : null}
      <Notice>{strings.tableSource}</Notice>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: strings.cellE0,
          value: volts(cell.e0),
        })}
      />
    </>
  );
}

export function CellPanel({
  strings,
  sigChoice,
}: {
  strings: NernstStrings;
  sigChoice: string;
}) {
  const fmt = useToolNumber();
  const [cathode, setCathode] = useState("cu");
  const [anode, setAnode] = useState("zn");
  const [tText, setTText] = useState("25.0");
  const [tUnit, setTUnit] = useState("C");
  const [qText, setQText] = useState("");
  const sig = sigChoice === "match" ? DEFAULT_SIG : Number(sigChoice);
  const options = HALF_CELLS.map((cell) => ({
    value: cell.id,
    label: `${cell.couple}  (${fmt(cell.e0, { minimumFractionDigits: 2, maximumFractionDigits: 5, signDisplay: "exceptZero" })} V)`,
  }));
  const typedT = parseMeasuredNumber(tText);
  const tKelvin = typedT === null ? null : toKelvin(typedT, tUnit);
  const tProblem = tKelvin === null || tKelvin <= 0;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.cathodeHint}
          id="cell-cathode"
          label={strings.cathode}
          onChange={setCathode}
          options={options}
          value={cathode}
        />
        <SelectField
          hint={strings.anodeHint}
          id="cell-anode"
          label={strings.anode}
          onChange={setAnode}
          options={options}
          value={anode}
        />
        <QuantityInput
          id="cell-t"
          label={strings.termT}
          onUnitChange={setTUnit}
          onValueChange={setTText}
          unit={tUnit}
          unitLabel={strings.unitTemperature}
          units={TEMPERATURE_OPTIONS}
          value={tText}
        />
        <TextField
          hint={strings.cellQHint}
          id="cell-q"
          label={strings.cellQ}
          numeric
          onChange={setQText}
          value={qText}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {tProblem ? (
          <FailureNotice
            failure={{ reason: temperatureProblem(tText, tKelvin), key: "t" }}
            labels={{ t: strings.termT }}
            messages={electroMessages(strings)}
          />
        ) : (
          <CellAnswer
            anodeId={anode}
            cathodeId={cathode}
            qText={qText}
            sig={sig}
            strings={strings}
            tText={tText}
            tUnit={tUnit}
          />
        )}
      </section>
    </>
  );
}
